// Data-owning card for the Workflow Engine (WFL) tenant-CUSTOMIZATION surface. Three top-tab
// nav: Statuses & Labels, Approvals & Gates, Authority Assignment.
// Rendered only as a side effect of a SAM tool call — there is no dedicated settings route.
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/utils/cn';
import { apiErrorMessage } from '@/utils/apiError';
import { Button } from '@/components/UI/Button';
import { Spinner } from '@/components/UI/Spinner';
import {
  useGetWorkflowCustomizationQuery,
  useToggleWorkflowItemMutation,
  useRenameWorkflowLabelMutation,
  useSetTransitionAuthorityMutation,
  useSetGateApprovalSettingsMutation,
  useSetWorkflowAccessPolicyMutation,
  type AccessPolicy,
} from '@/redux/internal-services/workflowEngineApi';
import {
  ROLE_OPTIONS, FIELD_CLS, humanize, Toggle, RenameLabel,
} from '@/components/Workflow/workflowSettingsShared';

type NavTab = 'statuses' | 'approvals' | 'authority' | 'access';

const NAV_TABS: Array<{ id: NavTab; label: string }> = [
  { id: 'statuses', label: 'Statuses & Labels' },
  { id: 'approvals', label: 'Approvals & Gates' },
  { id: 'authority', label: 'Authority Assignment' },
  { id: 'access', label: 'Access Policy' },
];

type AccessScopeKey = string; // "default" | `stage:${code}` | `process:${code}`

const APPROVER_ROLE_OPTIONS = [
  { value: '', label: 'Any authorized role' },
  { value: 'tenant_admin', label: 'Business Owner' },
  { value: 'project_manager', label: 'Project Manager' },
  { value: 'office_admin', label: 'Office Manager' },
  { value: 'sales_agent', label: 'Sales Agent' },
  { value: 'crew_member', label: 'Crew Member' },
];

export interface WorkflowSettingsCardProps {
  rev?: number;
  onMeta?: (meta: { title: string }) => void;
  /** Reports the visible tab + gate/status/transition codes to the agent context so SAM knows exact codes. */
  onStateChange?: (state: {
    tab: NavTab;
    gates: Array<{ gate_code: string; display: string; is_active: boolean; is_optional: boolean }>;
    statuses: Array<{ status_code: string; name: string; is_active: boolean; is_optional: boolean }>;
    transitions?: Array<{ transition_code: string; action_label: string; from_status: string; to_status: string; allowed_roles: string[] }>;
  } | null) => void;
  className?: string;
}

export const WorkflowSettingsCard: React.FC<WorkflowSettingsCardProps> = ({ rev, onMeta, onStateChange, className }) => {
  const { t } = useTranslation();
  const { data, isLoading, isError, refetch } = useGetWorkflowCustomizationQuery();
  const [activeTab, setActiveTab] = useState<NavTab>('statuses');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (rev === undefined) return;
    void refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rev]);

  useEffect(() => {
    const title = data?.name ? `${data.name} — How Your Team Works` : 'How Your Team Works';
    onMeta?.({ title });
  }, [data?.name, onMeta]);

  // Emit gate + status + transition codes whenever the tab or data changes — lets the agent read
  // exact codes from context instead of guessing from display names.
  useEffect(() => {
    if (!data || !onStateChange) return;
    const statusName = (code: string) => data.statuses.find((s) => s.status_code === code)?.name ?? code;
    onStateChange({
      tab: activeTab,
      gates: (data.governance_gates ?? []).map((g) => ({
        gate_code: g.gate_code,
        display: String(g.condition_type ?? '').replace(/_/g, ' '),
        is_active: Boolean(g.is_active),
        is_optional: Boolean(g.is_optional || g.is_custom),
      })),
      statuses: (data.statuses ?? []).map((s) => ({
        status_code: s.status_code,
        name: s.name ?? '',
        is_active: Boolean(s.is_active),
        is_optional: Boolean(s.is_optional || s.is_custom),
      })),
      transitions: (data.authority_rules ?? []).map((rule) => {
        const transCode = rule.target_refs?.[0] ?? '';
        const trans = (data.transitions ?? []).find((t) => t.transition_code === transCode);
        return {
          transition_code: transCode,
          action_label: trans?.action_label ?? humanize(rule.rule_code),
          from_status: trans ? statusName(trans.source_status_code) : '',
          to_status: trans ? statusName(trans.target_status_code) : '',
          allowed_roles: rule.allowed_roles ?? [],
        };
      }),
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, activeTab]);

  const withError = async (fn: () => Promise<unknown>): Promise<boolean> => {
    setError(null);
    try { await fn(); return true; }
    catch (e) { setError(apiErrorMessage(e, t, 'Could not complete that change.')); return false; }
  };

  const [toggleItem, { isLoading: toggling }] = useToggleWorkflowItemMutation();
  const [renameLabel, { isLoading: renaming }] = useRenameWorkflowLabelMutation();
  const [setAuthority, { isLoading: settingAuthority }] = useSetTransitionAuthorityMutation();
  const [setGateApproval, { isLoading: settingGateApproval }] = useSetGateApprovalSettingsMutation();
  const [setAccessPolicy, { isLoading: settingAccessPolicy }] = useSetWorkflowAccessPolicyMutation();

  const toggle = (itemType: 'status' | 'transition' | 'gate' | 'hook', itemCode: string, active: boolean) =>
    void withError(() => toggleItem({ itemType, itemCode, active }).unwrap());
  const rename = (itemType: 'stage' | 'process' | 'status' | 'transition', itemCode: string, newLabel: string) =>
    void withError(() => renameLabel({ itemType, itemCode, newLabel }).unwrap());

  const mandatoryTitle = 'Mandatory platform item — cannot be toggled.';

  // ── Authority editor ─────────────────────────────────────────────────────────
  const [authorityEditingCode, setAuthorityEditingCode] = useState<string | null>(null);
  const [authorityRoles, setAuthorityRoles] = useState<string[]>([]);
  const openAuthorityEditor = (transitionCode: string) => {
    const existing = (data?.authority_rules ?? []).find((r) => r.target_refs?.includes(transitionCode));
    setAuthorityRoles(existing?.allowed_roles ?? []);
    setAuthorityEditingCode(transitionCode);
  };
  const submitAuthority = async () => {
    if (!authorityEditingCode) return;
    const ok = await withError(() => setAuthority({ transitionCode: authorityEditingCode, allowedRoles: authorityRoles }).unwrap());
    if (ok) setAuthorityEditingCode(null);
  };

  const statusLabel = useCallback((code: string) => data?.statuses.find((s) => s.status_code === code)?.name ?? code, [data]);

  // ── Access Policy editor (default / stage / process — cascading, narrow-only) ──────────────
  const [accessEditingKey, setAccessEditingKey] = useState<AccessScopeKey | null>(null);
  const [accessRoles, setAccessRoles] = useState<string[]>([]);
  const [accessAssignmentRequired, setAccessAssignmentRequired] = useState(false);
  const policyForScope = (key: AccessScopeKey): AccessPolicy | null | undefined => {
    if (key === 'default') return data?.default_access_policy;
    if (key.startsWith('stage:')) return data?.stages.find((s) => s.stage_code === key.slice(6))?.access_policy;
    return data?.processes.find((p) => p.process_code === key.slice(8))?.access_policy;
  };
  const openAccessEditor = (key: AccessScopeKey) => {
    const existing = policyForScope(key);
    setAccessRoles(existing?.allowed_roles ?? []);
    setAccessAssignmentRequired(existing?.assignment_required ?? false);
    setAccessEditingKey(key);
  };
  const submitAccessPolicy = async () => {
    if (!accessEditingKey) return;
    const scope: 'default' | 'stage' | 'process' = accessEditingKey === 'default' ? 'default' : accessEditingKey.startsWith('stage:') ? 'stage' : 'process';
    const scopeCode = scope === 'default' ? undefined : accessEditingKey.slice(scope === 'stage' ? 6 : 8);
    const ok = await withError(() => setAccessPolicy({ scope, scopeCode, allowedRoles: accessRoles, assignmentRequired: accessAssignmentRequired }).unwrap());
    if (ok) setAccessEditingKey(null);
  };
  const accessSummary = (policy: AccessPolicy | null | undefined) => {
    if (!policy || (policy.allowed_roles.length === 0 && !policy.assignment_required)) return 'No restriction — set access';
    const parts: string[] = [];
    if (policy.allowed_roles.length) parts.push(policy.allowed_roles.map((r) => humanize(r)).join(', '));
    if (policy.assignment_required) parts.push('Assigned user only');
    return parts.join(' · ');
  };

  if (isLoading && !data) {
    return (
      <div className={cn('flex items-center justify-center gap-2 rounded-2xl border border-ui-border bg-ui-card p-8', className)}>
        <Spinner size="sm" /><span className="text-xs text-ui-text3">Loading company processes…</span>
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div className={cn('rounded-2xl border border-ui-border bg-ui-card p-4', className)}>
        <p className="text-xs text-[#CF4148]">{t('workflow.settings.companyProcessConfigUnavailable')}</p>
      </div>
    );
  }

  const optionalGates = data.governance_gates.filter((g) => g.is_optional || g.is_custom);
  const activeOptionalGates = optionalGates.filter((g) => g.is_active);

  const authorityRows = data.authority_rules.map((rule) => {
    const isSystemOnly = rule.system_allowed && (!rule.allowed_roles || rule.allowed_roles.length === 0);
    const transCode = rule.target_refs?.[0] ?? '';
    const trans = data.transitions.find((t) => t.transition_code === transCode);
    return { rule, trans, transCode, isSystemOnly };
  });

  return (
    <div className={cn('flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border border-ui-border bg-ui-card shadow-sm', className)}>

      {/* Top tab nav */}
      <div className="flex shrink-0 border-b border-ui-border">
        {NAV_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              'px-4 py-2.5 text-xs font-medium transition-colors border-b-2 -mb-px',
              activeTab === tab.id
                ? 'border-brand-dark text-brand-dark'
                : 'border-transparent text-ui-text2 hover:text-ui-text hover:border-ui-border/60',
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Banners */}
      {data.source === 'platform' && (
        <div className="shrink-0 border-b border-amber-400/40 bg-amber-50 px-4 py-2 text-xs text-amber-800 dark:bg-amber-900/20 dark:text-amber-400">
          {t('workflowSettings.platformFallback', "Showing platform defaults — any change here creates your tenant's own version.")}
        </div>
      )}
      {error && (
        <div className="shrink-0 border-b border-[#CF4148]/40 bg-red-50 px-4 py-2 text-xs text-[#CF4148] dark:bg-red-900/20">{error}</div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">

        {/* ── STATUSES & LABELS ──────────────────────────────────────── */}
        {activeTab === 'statuses' && (
          <div>
            <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-800 dark:border-green-800/40 dark:bg-green-900/20 dark:text-green-400">
              You can rename permitted labels and switch optional statuses on or off. Stages and processes are locked by the platform.
            </div>

            <div className="mb-1 grid grid-cols-[1fr_1fr_80px_56px] gap-3 px-1">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-ui-text3">Platform Status</span>
              <span className="text-[10px] font-semibold uppercase tracking-wider text-ui-text3">Your Display Label</span>
              <span className="text-[10px] font-semibold uppercase tracking-wider text-ui-text3">Optional</span>
              <span className="text-[10px] font-semibold uppercase tracking-wider text-ui-text3">Active</span>
            </div>

            <div className="space-y-1.5">
              {[...data.statuses].sort((a, b) => a.display_order - b.display_order).map((status) => {
                const canToggle = status.is_optional || status.is_custom;
                return (
                  <div key={status.status_code} className="grid grid-cols-[1fr_1fr_80px_56px] items-center gap-3 rounded-lg border border-ui-border bg-ui-subtle/40 px-3 py-2.5">
                    <div className="min-w-0">
                      <p className={cn('truncate text-xs font-medium', !status.is_active && 'text-ui-text3 line-through')}>
                        {status.name}
                      </p>
                      <code className="font-mono text-[9px] text-ui-text3">{status.status_code.toUpperCase()}</code>
                    </div>
                    <div className="flex min-w-0 items-center gap-1">
                      <RenameLabel
                        code={status.status_code}
                        label={status.name}
                        renamable={status.tenant_label_renamable}
                        busy={renaming}
                        onRename={(c, l) => rename('status', c, l)}
                        className="text-xs"
                      />
                      {!status.tenant_label_renamable && (
                        <span title="Label rename not permitted by platform" className="shrink-0 text-[11px]">🔒</span>
                      )}
                    </div>
                    <span className={cn('text-[10px] font-medium', status.is_optional || status.is_custom ? 'text-brand-dark' : 'text-ui-text3')}>
                      {status.is_optional || status.is_custom ? 'optional' : 'required'}
                    </span>
                    <Toggle
                      checked={status.is_active}
                      disabled={!canToggle || toggling}
                      title={canToggle ? undefined : mandatoryTitle}
                      onClick={() => toggle('status', status.status_code, !status.is_active)}
                    />
                  </div>
                );
              })}
            </div>

            <p className="mt-3 text-[10px] text-ui-text3">
              🔒 = label rename not permitted by platform. Renaming never changes internal code, lifecycle role, or reporting meaning.
            </p>
          </div>
        )}

        {/* ── APPROVALS & GATES ──────────────────────────────────────── */}
        {activeTab === 'approvals' && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-ui-text3">Optional Governance Gates</p>
              <div className="space-y-2">
                {optionalGates.length === 0 && (
                  <p className="text-xs text-ui-text3">{t('workflow.settings.noOptionalGovernanceGates')}</p>
                )}
                {optionalGates.map((gate) => (
                  <div key={gate.gate_code} className="rounded-lg border border-ui-border bg-ui-subtle px-3 py-2.5">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-ui-text">{humanize(gate.condition_type)}</p>
                        <code className="font-mono text-[9px] text-ui-text3">{gate.gate_code}</code>
                        {gate.description && <p className="mt-0.5 text-[10px] text-ui-text3">{gate.description}</p>}
                      </div>
                      <Toggle
                        checked={gate.is_active}
                        disabled={toggling}
                        onClick={() => toggle('gate', gate.gate_code, !gate.is_active)}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-[10px] text-ui-text3">Only platform-approved optional gates appear here. Mandatory gates cannot be turned off.</p>
            </div>

            <div>
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-ui-text3">Approver Roles & Self-Approval</p>
              {activeOptionalGates.length === 0 && (
                <p className="text-xs text-ui-text3">{t('workflow.settings.enableGateToConfigure')}</p>
              )}
              <div className="space-y-3">
                {activeOptionalGates.map((gate) => (
                  <div key={gate.gate_code} className="rounded-lg border border-ui-border bg-ui-card p-3">
                    <p className="mb-2 text-xs font-semibold text-ui-text">{humanize(gate.condition_type)}</p>
                    <div className="space-y-2">
                      <div>
                        <p className="mb-1 text-[10px] text-ui-text3">Approver role</p>
                        <select
                          value={gate.approver_role ?? ''}
                          onChange={(e) => void withError(() =>
                            setGateApproval({
                              gateCode: gate.gate_code,
                              approverRole: e.target.value || null,
                              prohibitSelfApproval: gate.prohibit_self_approval ?? false,
                            }).unwrap()
                          )}
                          disabled={settingGateApproval}
                          className={cn(FIELD_CLS, 'w-full')}
                        >
                          {APPROVER_ROLE_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-ui-text2">Prohibit self-approval</span>
                        <Toggle
                          checked={gate.prohibit_self_approval ?? false}
                          disabled={settingGateApproval}
                          onClick={() => void withError(() =>
                            setGateApproval({
                              gateCode: gate.gate_code,
                              approverRole: gate.approver_role ?? null,
                              prohibitSelfApproval: !(gate.prohibit_self_approval ?? false),
                            }).unwrap()
                          )}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-[10px] text-ui-text3">Approver role selectable only from the platform-approved list for each type.</p>
            </div>
          </div>
        )}

        {/* ── AUTHORITY ASSIGNMENT ───────────────────────────────────── */}
        {activeTab === 'authority' && (
          <div>
            <div className="mb-3 grid grid-cols-[1fr_140px_100px_80px] gap-3 px-1">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-ui-text3">Action / Transition</span>
              <span className="text-[10px] font-semibold uppercase tracking-wider text-ui-text3">Authorized Roles</span>
              <span className="text-[10px] font-semibold uppercase tracking-wider text-ui-text3">Assignment</span>
              <span className="text-[10px] font-semibold uppercase tracking-wider text-ui-text3">System Exec</span>
            </div>

            <div className="space-y-1.5">
              {authorityRows.map(({ rule, trans, transCode, isSystemOnly }) => (
                <div key={rule.rule_code}>
                  <div className="grid grid-cols-[1fr_140px_100px_80px] items-center gap-3 rounded-lg border border-ui-border bg-ui-subtle/40 px-3 py-2.5">
                    <div className="min-w-0">
                      <p className="truncate text-xs font-medium text-ui-text">
                        {trans?.action_label ?? humanize(rule.rule_code)}
                      </p>
                      {trans && (
                        <p className="text-[10px] text-ui-text3">
                          {statusLabel(trans.source_status_code)} → {statusLabel(trans.target_status_code)}
                        </p>
                      )}
                      {!trans && rule.description && (
                        <p className="text-[10px] text-ui-text3">{rule.description}</p>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-1">
                      {isSystemOnly ? (
                        <span className="text-[10px] text-ui-text3">system actor</span>
                      ) : rule.allowed_roles?.length ? (
                        <>
                          {rule.allowed_roles.map((r) => (
                            <span key={r} className="inline-flex items-center rounded-full bg-brand-dark/10 px-2 py-0.5 text-[10px] font-medium text-brand-dark dark:bg-brand-dark/20">
                              {humanize(r.replace(/_/g, ' '))}
                            </span>
                          ))}
                          <button
                            type="button"
                            className="text-[10px] text-ui-text3 hover:text-brand-dark hover:underline"
                            onClick={() => openAuthorityEditor(transCode)}
                          >
                            Edit
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className="text-[10px] text-brand-dark hover:underline"
                          onClick={() => openAuthorityEditor(transCode)}
                        >
                          Assign roles
                        </button>
                      )}
                    </div>

                    <span className="text-[10px] text-ui-text2">
                      {isSystemOnly ? 'n/a' : (rule.assignment_scope ?? 'any').replace(/_/g, ' ')}
                    </span>

                    {rule.system_allowed ? (
                      <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                        enabled
                      </span>
                    ) : (
                      <span className="text-[10px] text-ui-text3">—</span>
                    )}
                  </div>

                  {authorityEditingCode === transCode && (
                    <div className="ml-2 mt-1 rounded-lg border border-ui-border bg-ui-card p-3">
                      <p className="mb-2 text-[10px] font-medium text-ui-text2">Who can execute this transition</p>
                      <div className="flex flex-wrap gap-x-3 gap-y-1">
                        {ROLE_OPTIONS.map((r) => (
                          <label key={r} className="flex items-center gap-1 text-[11px] text-ui-text">
                            <input
                              type="checkbox"
                              checked={authorityRoles.includes(r)}
                              onChange={(e) =>
                                setAuthorityRoles((prev) =>
                                  e.target.checked ? [...prev, r] : prev.filter((x) => x !== r)
                                )
                              }
                            />
                            {humanize(r)}
                          </label>
                        ))}
                      </div>
                      <div className="mt-2 flex justify-end gap-2">
                        <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => setAuthorityEditingCode(null)}>
                          {t('workflow.cancel', 'Cancel')}
                        </Button>
                        <Button size="sm" className="h-7 px-2" loading={settingAuthority} onClick={() => void submitAuthority()}>
                          {t('workflowSettings.save', 'Save')}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {authorityRows.length === 0 && (
                <p className="text-xs text-ui-text3">{t('workflow.settings.noAuthorityRules')}</p>
              )}
            </div>

            <div className="mt-4 grid grid-cols-3 gap-2">
              {[
                ['Different transitions from one status can require', 'different roles', '.'],
                ['SAM acts under the initiating user — never more authority.', '', ''],
                ['You may assign only the', 'platform-approved', ' roles & authority types.'],
              ].map(([pre, bold, post], i) => (
                <div key={i} className="rounded-lg border border-ui-border bg-ui-subtle p-2.5 text-[10px] text-ui-text2">
                  {pre}{bold ? <strong>{bold}</strong> : null}{post}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── ACCESS POLICY ───────────────────────────────────────────── */}
        {activeTab === 'access' && (
          <div>
            <div className="mb-3 rounded-lg border border-ui-border bg-ui-subtle p-2.5 text-[10px] text-ui-text2">
              Each level only NARROWS what a broader level already allows — a stage or process can tighten the
              workflow default, never loosen it.
            </div>

            <div className="space-y-1.5">
              {([{ key: 'default', title: 'Workflow Default', subtitle: 'Applies to every engagement of this type' }] as Array<{ key: AccessScopeKey; title: string; subtitle: string }>)
                .concat([...data.stages].sort((a, b) => a.display_order - b.display_order).map((s) => ({ key: `stage:${s.stage_code}`, title: s.name, subtitle: 'Stage' })))
                .concat([...data.processes].sort((a, b) => a.display_order - b.display_order).map((p) => ({
                  key: `process:${p.process_code}`, title: p.name,
                  subtitle: data.stages.find((s) => s.stage_code === p.stage_code)?.name ?? p.stage_code,
                })))
                .map(({ key, title, subtitle }) => {
                  const policy = policyForScope(key);
                  return (
                    <div key={key}>
                      <div className="flex items-center justify-between gap-2 rounded-lg border border-ui-border bg-ui-subtle/40 px-3 py-2.5">
                        <div className="min-w-0">
                          <p className="truncate text-xs font-medium text-ui-text">{title}</p>
                          <p className="text-[10px] text-ui-text3">{subtitle}</p>
                        </div>
                        <button type="button" className="shrink-0 text-[10px] text-brand-dark hover:underline" onClick={() => openAccessEditor(key)}>
                          {accessSummary(policy)}
                        </button>
                      </div>
                      {accessEditingKey === key && (
                        <div className="ml-2 mt-1 rounded-lg border border-ui-border bg-ui-card p-3">
                          <div className="flex flex-wrap gap-x-3 gap-y-1">
                            {ROLE_OPTIONS.map((r) => (
                              <label key={r} className="flex items-center gap-1 text-[11px] text-ui-text">
                                <input type="checkbox" checked={accessRoles.includes(r)} onChange={(e) => setAccessRoles((prev) => (e.target.checked ? [...prev, r] : prev.filter((x) => x !== r)))} />
                                {humanize(r)}
                              </label>
                            ))}
                          </div>
                          <label className="mt-2 flex items-center gap-1.5 text-[11px] text-ui-text2">
                            <input type="checkbox" checked={accessAssignmentRequired} onChange={(e) => setAccessAssignmentRequired(e.target.checked)} />
                            Only the assigned user may act
                          </label>
                          <div className="mt-2 flex justify-end gap-2">
                            <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => setAccessEditingKey(null)}>{t('workflow.cancel', 'Cancel')}</Button>
                            <Button size="sm" className="h-7 px-2" loading={settingAccessPolicy} onClick={() => void submitAccessPolicy()}>{t('workflowSettings.save', 'Save')}</Button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default WorkflowSettingsCard;
